import { useState, useRef, useEffect } from 'react';
import { TextConversation } from '@elevenlabs/client';
import api from '@/utils/ky';
import { AIAKO_FAQ_AGENT_ID } from '@/utils/constants';

export enum TextChatState {
  INITIAL = 'initial',
  CONNECTING = 'connecting',
  READY = 'ready',
  ENDING = 'ending',
  ERROR = 'error',
}

export interface TranscriptEntry {
  source: 'user' | 'agent';
  message: string;
  timestamp: Date;
}

export const useAiakoTextChat = () => {
  const [chatState, setChatState] = useState<TextChatState>(TextChatState.INITIAL);
  const [error, setError] = useState<string>('');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [isChatActive, setIsChatActive] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [chatDuration, setChatDuration] = useState(0);

  const conversationRef = useRef<TextConversation | null>(null);
  const startTimeRef = useRef<number>(0);
  const chatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startChat = async () => {
    try {
      setChatState(TextChatState.CONNECTING);
      setError('');
      setTranscript([]);

      const { signedUrl } = await api
        .get(`api/elevenlabs/signed-url?agent_id=${AIAKO_FAQ_AGENT_ID}`)
        .json<{ signedUrl: string }>();

      const conversation = await TextConversation.startSession({
        signedUrl,
        onConnect: () => {
          setChatState(TextChatState.READY);
          setIsChatActive(true);
          startTimeRef.current = Date.now();
          chatTimerRef.current = setInterval(() => {
            setChatDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
          }, 1000);
        },
        onDisconnect: () => {
          setIsChatActive(false);
          setIsAgentTyping(false);
          setChatState(TextChatState.INITIAL);
          conversationRef.current = null;
          if (chatTimerRef.current) {
            clearInterval(chatTimerRef.current);
            chatTimerRef.current = null;
          }
        },
        onMessage: ({ source, message }: { source: 'user' | 'ai'; message: string }) => {
          if (message && source === 'ai') {
            setIsAgentTyping(false);
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
              typingTimeoutRef.current = null;
            }
            setTranscript(prev => [
              ...prev,
              { source: 'agent', message, timestamp: new Date() },
            ]);
          }
        },
        onError: (err: unknown) => {
          console.error('ElevenLabs text error:', err);
          setError('Connection error. Please try again.');
          setChatState(TextChatState.ERROR);
        },
      });

      conversationRef.current = conversation;
    } catch (err) {
      console.error('Failed to start Aiako text chat:', err);
      setError('Failed to connect. Please try again.');
      setChatState(TextChatState.ERROR);
    }
  };

  const sendMessage = (text: string) => {
    const trimmed = text.trim();
    if (!conversationRef.current || !trimmed) return;
    setTranscript(prev => [...prev, { source: 'user', message: trimmed, timestamp: new Date() }]);
    conversationRef.current.sendUserMessage(trimmed);
    setIsAgentTyping(true);
    typingTimeoutRef.current = setTimeout(() => setIsAgentTyping(false), 30000);
  };

  const endChat = () => {
    setChatState(TextChatState.ENDING);
    if (conversationRef.current) {
      conversationRef.current.endSession();
      conversationRef.current = null;
    }
    setIsChatActive(false);
    setIsAgentTyping(false);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      if (chatTimerRef.current) clearInterval(chatTimerRef.current);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (conversationRef.current) conversationRef.current.endSession();
    };
  }, []);

  return {
    chatState,
    error,
    transcript,
    isChatActive,
    isAgentTyping,
    chatDuration,
    startChat,
    sendMessage,
    endChat,
    formatDuration,
  };
};
